import { Menu, Moon, Smartphone, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import MenuSidebar from '../../layouts/MenuSidebar';
import TopBar from '../../layouts/TopBar';
import { darkTheme, lightTheme } from '../../theme/theme';
import useTheme from '../../hooks/useTheme';
import LoadingSpinner from '../../components/common/LoadingSpinner';

import { getActiveProduct, getProductDataFolder, PRODUCTS } from '../../utils/urlConfig';

const TemperatureGraph = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { productId: urlProductId, serialNumber: urlSerialNumber } = useParams();
    const [isDark, setIsDark] = useTheme();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isLandscape, setIsLandscape] = useState(false);
    const [stops, setStops] = useState(location.state?.stops || []);
    const [activeProduct, setActiveProductState] = useState('');
    const theme = isDark ? darkTheme : lightTheme;
    
    useEffect(() => {
        if (urlProductId && urlSerialNumber) {
            let product = getActiveProduct();
            for (const [key, config] of Object.entries(PRODUCTS)) {
                if (config.gtin === urlProductId) {
                    product = key;
                    break;
                }
            }
            setActiveProductState(product);
        } else {
            setActiveProductState(getActiveProduct());
        }
    }, [urlProductId, urlSerialNumber]);

    // Load route data
    useEffect(() => {
        const loadRouteData = async () => {
            try {
                const dataFolder = getProductDataFolder(activeProduct);
                const data = await import(`../../data/${dataFolder}/map_route.json`);
                const points = data.default || data;
                // Always use map_route data for temperatures
                setStops(points);
            } catch (error) {
                console.error('Error loading route data:', error);
            }
        };
        loadRouteData();
    }, [activeProduct]);

    // Process data for graph
    const dataPoints = stops.map(stop => {
        const tempVal = stop.temperature ? parseFloat(stop.temperature) : null;
        // Clamp temperature to be within 0-28°C
        const clampedTemp = tempVal !== null ? Math.max(0, Math.min(28, tempVal)) : null;
        return { ...stop, tempVal: clampedTemp };
    }).filter(d => d.tempVal !== null);

    const temps = dataPoints.map(d => d.tempVal);
    const avgTemp = temps.length
        ? temps.reduce((sum, t) => sum + t, 0) / temps.length
        : 0;

    // Fixed boundaries: 28°C high limit, 0°C low limit
    const maxTempLimit = 28;
    const minTempLimit = 0;

    const chartData = dataPoints.map(point => ({
        name: point.label,
        temp: point.tempVal,
        title: point.title,
        displayTemp: point.temperature,
    }));

    // Set domain to fixed boundaries
    const yDomain = [minTempLimit, maxTempLimit];

    const formatTemp = (value) => `${Math.round(Number(value))}°C`;

    return (
        <div
            className="flex flex-col h-screen w-full relative overflow-hidden"
            style={{
                backgroundColor: theme.background1,
                ...(isLandscape ? {
                    transform: 'rotate(90deg)',
                    transformOrigin: 'center center',
                    width: '100vh',
                    height: '100vw',
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    marginTop: 'calc(-50vw)',
                    marginLeft: 'calc(-50vh)',
                } : {})
            }}
        >
            <TopBar
                theme={theme}
                isDark={isDark}
                setIsDark={setIsDark}
                title="Temperature Graph"
                onBackClick={() => {
                    const backPath = urlProductId && urlSerialNumber 
                        ? `/01/${urlProductId}/21/${urlSerialNumber}/ccmap`
                        : `/01/${PRODUCTS[activeProduct]?.gtin}/21/${PRODUCTS[activeProduct]?.serialNumber}/ccmap`;
                    navigate(backPath, { state: { isDark } });
                }}
                isMenuOpen={isMenuOpen}
                setIsMenuOpen={setIsMenuOpen}
                fixed={false}
                backgroundColor={theme.background1}
                customRightActions={
                    <>
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="w-10 h-10 rounded-full hidden md:flex items-center justify-center"
                            style={{ color: theme.text }}
                        >
                            <Menu size={20} />
                        </button>

                        <button
                            onClick={() => setIsLandscape(!isLandscape)}
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ color: theme.text }}
                        >
                            <Smartphone size={20} style={isLandscape ? { transform: 'rotate(90deg)' } : {}} />
                        </button>

                        <button
                            onClick={() => setIsDark(!isDark)}
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ color: theme.text }}
                        >
                            {isDark ? <Moon size={20} /> : <Sun size={20} />}
                        </button>
                    </>
                }
            />

            {/* Graph Content */}
            <div className="flex-1 p-6 relative flex flex-col gap-4 overflow-y-auto">
                {stops.length === 0 && <LoadingSpinner />}
                {dataPoints.length > 0 ? (
                    <div
                        className="rounded-2xl border p-4"
                        style={{
                            backgroundColor: theme.background2, borderColor: theme.stroke,
                        }}
                    >
                        <div className="h-64 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                                    
                                    <CartesianGrid strokeDasharray="3 3" stroke={theme.stroke} />
                                    <XAxis
                                        dataKey="name"
                                        stroke={theme.text}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <YAxis
                                        domain={yDomain}
                                        tickFormatter={formatTemp}
                                        stroke={theme.text}
                                        tickLine={false}
                                        axisLine={false}
                                        width={50}
                                        tickCount={8}
                                        allowDecimals={false}
                                    />
                                    <Tooltip
                                        cursor={{ stroke: '#94A3B8', strokeDasharray: '4 4' }}
                                        contentStyle={{
                                            backgroundColor: theme.background2,
                                            borderColor: theme.stroke,
                                            color: theme.text,
                                        }}
                                        labelStyle={{ color: theme.text }}
                                        formatter={(value) => formatTemp(value)}
                                    />
                                    <ReferenceLine y={maxTempLimit} stroke="#EF4444" strokeDasharray="4 4" />
                                    <ReferenceLine y={minTempLimit} stroke="#22C55E" strokeDasharray="4 4" />
                                    <ReferenceLine y={avgTemp} stroke="#3B82F6" strokeDasharray="4 4" />
                                    <Area
                                        type="monotone"
                                        dataKey="temp"
                                        stroke="#3B82F6"
                                        strokeWidth={2}
                                        fill="url(#tempArea)"
                                        dot={{ r: 3, fill: '#0EA5E9' }}
                                        activeDot={{ r: 5 }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                ) : (
                    <div className="text-center text-gray-500">No temperature data available</div>
                )}

                {dataPoints.length > 0 && (
                    <div
                        className="rounded-2xl border px-4 py-3 flex items-center justify-center gap-6 text-xs font-semibold"
                        style={{
                            backgroundColor: theme.background2,
                            borderColor: theme.stroke,
                            color: theme.text,
                        }}
                    >
                        <div className="flex items-center gap-2">
                            <span className="h-1.5 w-6 rounded-full" style={{ backgroundColor: '#EF4444' }}></span>
                            <span>High (28°C)</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="h-1.5 w-6 rounded-full" style={{ backgroundColor: '#22C55E' }}></span>
                            <span>Low (0°C)</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="h-1.5 w-6 rounded-full" style={{ backgroundColor: '#3B82F6' }}></span>
                            <span>Average</span>
                        </div>
                    </div>
                )}

                {/* Route List*/}
                {dataPoints.length > 0 && (
                    <div className="space-y-3 pb-4">
                        {dataPoints.map((dataPoint, index) => (
                            <div
                                key={dataPoint.id}
                                className="flex items-center gap-4 p-3 rounded-xl border"
                                style={{
                                    backgroundColor: theme.background2,
                                    borderColor: theme.stroke,
                                }}
                            >
                                <div
                                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                                    style={{
                                        backgroundColor:
                                            dataPoint.tempVal >= 26
                                                ? '#EF4444'
                                                : dataPoint.tempVal <= 2
                                                ? '#22C55E'
                                                : '#3B82F6',
                                    }}
                                >
                                    {dataPoint.label}
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm font-medium" style={{ color: theme.text }}>
                                        {dataPoint.title} ({dataPoint.subtitle})
                                    </p>
                                    {dataPoint.tempVal !== null && (
                                        <p className="text-base font-bold" style={{ color: theme.text }}>
                                            {formatTemp(dataPoint.tempVal)}
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Menu Sidebar */}
            <MenuSidebar
                theme={theme}
                isDark={isDark}
                isOpen={isMenuOpen}
                onClose={() => setIsMenuOpen(false)}
            />
        </div>
    );
};

export default TemperatureGraph; 